/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { HighchartsErrorException } from 'reporting-data/exceptions';
import I18n from 'I18n';
import { gate } from 'hub-http/gates';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { Flex } from 'foundations-components/components/Flex';

// This allows us to support Webpack 3 and 4.
// Once all apps are on Webpack 4, this import can be simplified
// https://product.hubteam.com/docs/frontend/kb/webpack4.html
import { jsx as _jsx } from "react/jsx-runtime";
const getImport = imported => {
  if (imported.default) return imported.default;
  return imported;
};
const DisableManualHighchartReflowGate = gate('ReportingViz:DisableHighchartManualReflow');
export default class Highchart extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      width: undefined,
      height: undefined,
      error: null
    };
    this.instantiateNewChart = () => {
      try {
        import( /* webpackChunkName: "highcharts" */'highcharts').then(getImport).then(Highcharts => {
          if (typeof Highcharts === 'object') {
            Highcharts.setOptions({
              lang: {
                resetZoom: I18n.text('reporting-visualizations.resetZoom')
              }
            });
            Highcharts.SVGRenderer.prototype.symbols.tick = function (x, y, w, h) {
              return ['M', x - w / 2, y + h / 2, 'L', x + w + w / 2, y + h / 2, 'z'];
            };
          }
          const {
            chartOptions,
            setExtremesForZeroState,
            onReportVisualize
          } = this.props;
          const {
            chartRef
          } = this.refs;
          const options = chartOptions.toJS ? chartOptions.toJS() : chartOptions;
          options.chart = Object.assign({}, options.chart || {}, {
            renderTo: chartRef
          });
          this.chartInstance = new Highcharts.Chart(options, onReportVisualize);

          // https://stackoverflow.com/questions/13991873
          const isZeroState = this.chartInstance.yAxis.every(axis => axis.dataMax === 0 && axis.dataMin === 0);
          if (isZeroState && setExtremesForZeroState) {
            this.chartInstance.yAxis[0].setExtremes(0, 1);
          }
        }).catch(error => {
          this.setState({
            error
          });
        });
      } catch (error) {
        console.error(error);
      }
    };
  }
  componentDidMount() {
    this.instantiateNewChart();
    const {
      gates
    } = this.context;
    const disableManualReflowGate = gates === null || gates === void 0 ? void 0 : gates.includes(DisableManualHighchartReflowGate);
    this.watchDimensions = disableManualReflowGate ? null : setInterval(() => {
      const element = this.refs.chartRef;
      if (element) {
        const {
          width,
          height
        } = this.state;
        const {
          offsetWidth,
          offsetHeight
        } = element;
        if ((offsetWidth !== width || offsetHeight !== height) && this.chartInstance) {
          this.chartInstance.reflow();
          this.setState({
            width: offsetWidth,
            height: offsetHeight
          });
        }
      }
    }, 250);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.chartOptions.equals && !prevProps.chartOptions.equals(this.props.chartOptions) || prevProps.chartOptions !== this.props.chartOptions) {
      import( /* webpackChunkName: "highcharts" */'highcharts').then(getImport).then(() => {
        if (this.chartInstance && this.chartInstance.renderer && this.chartInstance.renderTo) {
          this.chartInstance.destroy();
          this.instantiateNewChart();
        }
      });
    }
  }
  componentWillUnmount() {
    clearInterval(this.watchDimensions);
    if (this.chartInstance) {
      import( /* webpackChunkName: "highcharts" */'highcharts').then(getImport).then(() => {
        if (this.chartInstance.renderer && this.chartInstance.renderTo) {
          this.chartInstance.destroy();
        }
      });
    }
  }
  render() {
    if (this.state.error) {
      throw new HighchartsErrorException(this.state.error);
    }
    return /*#__PURE__*/_jsx(Flex, {
      flexGrow: "1",
      children: /*#__PURE__*/_jsx("div", {
        className: "highcharts-root",
        id: "highcharts-root",
        ref: ref => {
          if (ref === null) {
            delete this.refs.chartRef;
          } else {
            this.refs.chartRef = ref;
          }
        },
        style: {
          width: '100%'
        }
      })
    });
  }
}
Highchart.contextType = UserInfoContext;
Highchart.propTypes = {
  chartOptions: PropTypes.object.isRequired,
  setExtremesForZeroState: PropTypes.bool,
  onReportVisualize: PropTypes.func
};
Highchart.defaultProps = {
  setExtremesForZeroState: true
};